import styled from "@emotion/styled";
import { FC, useEffect, useRef, useState } from "react";

import { DeviceDesktop, DeviceMobile, OpenInNew } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Icon,
  SegmentedControl,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { config } from "config/config";
import { PreviewType } from "routes/settings/constants";
import { IContactForm } from "store/entities/contact-forms/interfaces";
import { withVar } from "themes/helpers";

import { generatePreview } from "../helpers";

export interface IProps {
  contactForm: IContactForm;
}

enum PreviewWidth {
  Desktop = "unset",
  Mobile = "320px",
}

export const ContactFormPreview: FC<IProps> = ({ contactForm }) => {
  const [previewWidth, setPreviewWidth] = useState<PreviewWidth>(PreviewWidth.Desktop);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const generatedPreviewQueryString = generatePreview(contactForm, false, 2);
  const previewURL = `${config.contactFormsRootUrl}?${generatedPreviewQueryString}`;
  const finalURL = `${config.contactFormsRootUrl}?isPreview=1&version=2`;

  useEffect(() => {
    const sendMessage = (): void => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: "update_contact_form", data: contactForm }, "*");
      }
    };

    if (iframeLoaded) {
      const timeoutId = setTimeout(sendMessage, 100);
      return (): void => clearTimeout(timeoutId);
    }
  }, [contactForm, iframeLoaded]);

  const handlePreviewWidthChange = (id: string): void => {
    switch (id as PreviewType) {
      case PreviewType.Desktop:
        setPreviewWidth(PreviewWidth.Desktop);
        break;
      case PreviewType.Mobile:
        setPreviewWidth(PreviewWidth.Mobile);
        break;
      default:
        setPreviewWidth(PreviewWidth.Desktop);
    }
  };
  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        css={{
          padding: `6px ${withVar(SpacingToken.Spacing4)}`,
          borderBottom: `1px solid ${withVar(DesignToken.BorderBasicSecondary)}`,
          position: "sticky",
          top: 0,
          backgroundColor: withVar(DesignToken.SurfacePrimaryDefault),
        }}
      >
        <Text bold css={{ width: "141px" }}>
          Preview
        </Text>
        <SegmentedControl
          buttons={[
            { id: PreviewType.Desktop, label: "", icon: <Icon source={DeviceDesktop} data-testid="desktop-icon" /> },
            { id: PreviewType.Mobile, label: "", icon: <Icon source={DeviceMobile} data-testid="mobile-icon" /> },
          ]}
          initialId={PreviewType.Desktop}
          onButtonClick={handlePreviewWidthChange}
        />
        <Button
          icon={<Icon source={OpenInNew} data-testid="open-in-new-icon" />}
          iconPosition="right"
          kind="plain"
          size="xcompact"
          css={{ width: "141px" }}
          href={previewURL}
          target="_blank"
          rel="noopener noreferrer"
        >
          See in new tab
        </Button>
      </Flex>
      <IframeContainer width={previewWidth} data-testid="iframe-container">
        <iframe
          sandbox="allow-scripts allow-popups allow-forms allow-same-origin"
          width="100%"
          onLoad={() => {
            setIframeLoaded(true);
          }}
          ref={iframeRef}
          style={{ border: 0, background: "transparent", overflowX: "auto" }}
          src={finalURL}
          data-testid="form-preview"
          key="form-preview"
        >
          Your browser does not allow embedded content.
        </iframe>
      </IframeContainer>
    </>
  );
};

const IframeContainer = styled.div<{ width: PreviewWidth }>`
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
  margin: auto;
  width: ${({ width }): string => width};
  padding: ${withVar(SpacingToken.Spacing12)};
  padding-bottom: 120px;
  iframe {
    height: calc(100vh - 190px);
  }
`;
